/**
 * Builds a compact page list: first 3 … window around current … last 3.
 * Neighbors of the current page are always included so prev/next targets
 * (e.g. 4 when on 3, or 4543 when on 4544) stay visible and clickable.
 *
 * Examples:
 *   (current=1, total=10)  → [1, 2, 3, "ellipsis", 8, 9, 10]
 *   (current=3, total=20)  → [1, 2, 3, 4, "ellipsis", 18, 19, 20]
 *   (current=18, total=20) → [1, 2, 3, "ellipsis", 17, 18, 19, 20]
 *   (current=1, total=5)   → [1, 2, 3, 4, 5]
 */
export type PaginationItem = number | "ellipsis";

/** Max pages listed at each edge of the control. */
export const PAGINATION_EDGE_PAGES = 3;

/**
 * Max pages the server will walk in a single jump from either end.
 * Keeps direct opens of nearby pages (4, N-3, …) fast enough for interactive use.
 */
export const PAGINATION_MAX_WALK = 25;

export function buildPaginationItems(
  currentPage: number,
  knownPageCount: number,
): PaginationItem[] {
  const total = Math.max(1, knownPageCount);
  const current = Math.min(Math.max(1, currentPage), total);

  if (total <= PAGINATION_EDGE_PAGES * 2 + 1) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages = new Set<number>();

  for (let page = 1; page <= PAGINATION_EDGE_PAGES; page += 1) {
    pages.add(page);
  }
  for (let page = total - PAGINATION_EDGE_PAGES + 1; page <= total; page += 1) {
    pages.add(page);
  }

  // Current page + immediate neighbors (prev/next targets).
  for (let page = current - 1; page <= current + 1; page += 1) {
    if (page >= 1 && page <= total) pages.add(page);
  }

  const sorted = [...pages].sort((a, b) => a - b);
  const items: PaginationItem[] = [];

  for (let i = 0; i < sorted.length; i += 1) {
    const page = sorted[i]!;
    if (i > 0) {
      const prev = sorted[i - 1]!;
      if (page - prev > 1) {
        items.push("ellipsis");
      }
    }
    items.push(page);
  }

  return items;
}

/** True when a page can be opened via a bounded start/end walk. */
export function canWalkToPage(page: number, totalPages: number): boolean {
  const total = Math.max(1, totalPages);
  if (page < 1 || page > total) return false;
  const fromStart = page;
  const fromEnd = total - page + 1;
  return Math.min(fromStart, fromEnd) <= PAGINATION_MAX_WALK;
}

/**
 * Whether a page number is a direct jump target (edge pages, or any page
 * when the total fits on one control without ellipsis).
 */
export function isDirectJumpPage(page: number, totalPages: number): boolean {
  const total = Math.max(1, totalPages);
  if (page < 1 || page > total) return false;
  if (total <= PAGINATION_EDGE_PAGES * 2 + 1) return true;
  if (page <= PAGINATION_EDGE_PAGES || page > total - PAGINATION_EDGE_PAGES) return true;
  return canWalkToPage(page, total);
}

/**
 * Prefer fetching from the end when that walk is shorter than from the start.
 */
export function shouldFetchFromEnd(page: number, totalPages: number): boolean {
  const total = Math.max(1, totalPages);
  if (page < 1 || page > total) return false;
  if (total <= PAGINATION_EDGE_PAGES * 2 + 1) return false;
  return pageToEndDepth(page, total) < page;
}

/** 1-based depth from the end: last page → 1, second-to-last → 2, … */
export function pageToEndDepth(page: number, totalPages: number): number {
  return Math.max(1, Math.max(1, totalPages) - page + 1);
}

/** Size of the final (possibly partial) page. */
export function lastPageSize(totalCount: number, pageSize: number): number {
  const size = Math.max(1, pageSize);
  if (totalCount <= 0) return size;
  const remainder = totalCount % size;
  return remainder === 0 ? size : remainder;
}

/**
 * Page count for cursor-based logs pagination.
 * - Exact matchingCount: trust ceil(count / pageSize) only (never invent page 2
 *   when there are fewer than pageSize matches).
 * - Partial matchingCount: trust cursor navigation only (knownPageCount).
 */
export function estimatePageCount(
  matchingCount: number,
  pageSize: number,
  knownPageCount: number,
  matchingCountIsPartial: boolean,
): number {
  const size = Math.max(1, pageSize);

  if (!matchingCountIsPartial) {
    if (matchingCount <= 0) return 1;
    return Math.max(1, Math.ceil(matchingCount / size));
  }

  // Sample / partial totals must not create phantom pages (e.g. "2+" → page 2).
  return Math.max(1, knownPageCount);
}
